import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Brush
} from 'recharts';
import {
    Archive, Download, Calendar, Clock, Loader2, Inbox,
    AlertTriangle, LineChart as LineChartIcon, TrendingUp,
    Thermometer, CircleGauge, Droplets, Waves, Flame,
    Filter, Layers, Info, ChevronRight, Eye, EyeOff,
    FileSpreadsheet, CheckCircle2, XCircle
} from 'lucide-react';

// ============================================
// ✅ Константы
// ============================================
const NS_VALUE = -1; // Для Аксиома-01 НС = -1

const PARAM_CATEGORIES = {
    temperatura: { name: 'Температура', icon: Thermometer, unit: '°C', color: '#ef4444' },
    davlenie: { name: 'Давление', icon: CircleGauge, unit: 'МПа', color: '#06b6d4' },
    obemnui_rasxod: { name: 'Объёмный расход', icon: Droplets, unit: 'м³', color: '#a855f7' },
    massovui_rasxod: { name: 'Массовый расход', icon: Waves, unit: 'т', color: '#6366f1' },
    energi: { name: 'Энергия', icon: Flame, unit: 'Гкал', color: '#f97316' },
};

const CHART_COLORS = [
    '#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4',
    '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b',
    '#84cc16', '#0ea5e9', '#d946ef', '#64748b', '#dc2626'
];

// ============================================
// ✅ Проверка бита в битовой маске
// ============================================
const switchConvert = (bitPosition, switchValue) => {
    if (switchValue === null || switchValue === undefined) return false;
    const num = parseInt(switchValue);
    if (isNaN(num)) return false;
    return (num & Math.pow(2, bitPosition)) === Math.pow(2, bitPosition);
};

// ============================================
// ✅ Динамическое построение колонок
// ============================================
const buildArchiveColumns = (sampleRecord, archiveType) => {
    if (!sampleRecord) return [];

    const columns = [];
    columns.push({ key: 'datetime', name: 'Дата/Время', type: 'datetime' });
    columns.push({ key: 'ns', name: 'НС', type: 'ns' });

    if (switchConvert(0, sampleRecord.switch_time)) {
        columns.push({ key: 'time_work', name: 'Наработка (ВНo)', unit: 'час', type: 'time', source: 'time_work' });
    }
    if (switchConvert(1, sampleRecord.switch_time)) {
        columns.push({ key: 'energi_time_work_1', name: 'Наработка (ВНg)', unit: 'час', type: 'time', source: 'energi_time_work_1' });
    }

    if (switchConvert(0, sampleRecord.switch_time)) {
        columns.push({ key: 'time_NOT_work', name: 'Простой (ВПo)', unit: 'час', type: 'time', source: 'time_NOT_work' });
    }
    if (switchConvert(1, sampleRecord.switch_time)) {
        columns.push({ key: 'energi_time_NOT_work_1', name: 'Простой (ВПg)', unit: 'час', type: 'time', source: 'energi_time_NOT_work_1' });
    }

    const tempParams = [
        { key: 't1', name: 't1' }, { key: 't2', name: 't2' }, { key: 't3', name: 't3' }, { key: 't4', name: 't4' },
        { key: 'tx', name: 'tx' }, { key: 'dto', name: 'dto' }, { key: 'dtg', name: 'dtg' }, { key: 'tok', name: 'tok' },
    ];
    tempParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_temperatura)) {
            columns.push({ key: p.key, name: p.name, unit: '°C', type: 'value', category: 'temperatura' });
        }
    });

    const pressureParams = [
        { key: 'P1', name: 'P1' }, { key: 'P2', name: 'P2' }, { key: 'P3', name: 'P3' }, { key: 'P4', name: 'P4' },
        { key: 'Px', name: 'Px' }, { key: 'dPo', name: 'dPo' }, { key: 'dPg', name: 'dPg' },
    ];
    pressureParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_davlenie)) {
            columns.push({ key: p.key, name: p.name, unit: 'МПа', type: 'value', category: 'davlenie' });
        }
    });

    const volumeParams = [
        { key: 'Go1', name: 'Go1' }, { key: 'Go2', name: 'Go2' }, { key: 'Go3', name: 'Go3' }, { key: 'Go4', name: 'Go4' },
        { key: 'Gox', name: 'Gox' }, { key: 'Goo', name: 'Goo' }, { key: 'Gog', name: 'Gog' },
    ];
    volumeParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_rasxod)) {
            columns.push({ key: p.key, name: p.name, unit: 'м³', type: 'value', category: 'obemnui_rasxod' });
        }
    });

    const massParams = [
        { key: 'Gm1', name: 'Gm1' }, { key: 'Gm2', name: 'Gm2' }, { key: 'Gm3', name: 'Gm3' }, { key: 'Gm4', name: 'Gm4' },
        { key: 'Gmx', name: 'Gmx' }, { key: 'Gmo', name: 'Gmo' }, { key: 'Gmg', name: 'Gmg' },
    ];
    massParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_rasxod_massa)) {
            columns.push({ key: p.key, name: p.name, unit: 'т', type: 'value', category: 'massovui_rasxod' });
        }
    });

    const energyParams = [{ key: 'Qro', name: 'Qro' }, { key: 'Qrg', name: 'Qrg' }];
    energyParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_energi)) {
            columns.push({ key: p.key, name: p.name, unit: 'Гкал', type: 'value', category: 'energi' });
        }
    });

    return columns;
};

const buildIntegralColumns = (sampleRecord) => {
    if (!sampleRecord) return [];

    const columns = [];
    columns.push({ key: 'datetime', name: 'Дата/Время', type: 'datetime' });

    if (switchConvert(0, sampleRecord.switch_time)) {
        columns.push({ key: 'time_work', name: 'Наработка (ВНo)', unit: 'час', type: 'time', source: 'time_work' });
    }
    if (switchConvert(1, sampleRecord.switch_time)) {
        columns.push({ key: 'energi_time_work_in_min_1', name: 'Наработка (ВНg)', unit: 'час', type: 'time', source: 'energi_time_work_in_min_1' });
    }

    if (switchConvert(0, sampleRecord.switch_time)) {
        columns.push({ key: 'time_NOT_work', name: 'Простой (ВПo)', unit: 'час', type: 'time', source: 'time_NOT_work' });
    }
    if (switchConvert(1, sampleRecord.switch_time)) {
        columns.push({ key: 'energi_time_NOT_work_in_min_1', name: 'Простой (ВПg)', unit: 'час', type: 'time', source: 'energi_time_NOT_work_in_min_1' });
    }

    const volumeParams = [
        { key: 'V1', name: 'V1' }, { key: 'V2', name: 'V2' }, { key: 'V3', name: 'V3' }, { key: 'V4', name: 'V4' },
        { key: 'Vx', name: 'Vx' }, { key: 'Vo', name: 'Vo' }, { key: 'Vg', name: 'Vg' },
    ];
    volumeParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_rasxod)) {
            columns.push({ key: p.key, name: p.name, unit: 'м³', type: 'value', category: 'obemnui_rasxod' });
        }
    });

    const massParams = [
        { key: 'M1', name: 'M1' }, { key: 'M2', name: 'M2' }, { key: 'M3', name: 'M3' }, { key: 'M4', name: 'M4' },
        { key: 'Mx', name: 'Mx' }, { key: 'Mo', name: 'Mo' }, { key: 'Mg', name: 'Mg' },
    ];
    massParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_rasxod_massa)) {
            columns.push({ key: p.key, name: p.name, unit: 'т', type: 'value', category: 'massovui_rasxod' });
        }
    });

    const energyParams = [{ key: 'Qo', name: 'Qo' }, { key: 'Qg', name: 'Qg' }];
    energyParams.forEach((p, idx) => {
        if (switchConvert(idx, sampleRecord.switch_energi)) {
            columns.push({ key: p.key, name: p.name, unit: 'Гкал', type: 'value', category: 'energi' });
        }
    });

    return columns;
};

// ============================================
// ✅ Утилиты форматирования (запятая как разделитель)
// ============================================
const formatValue = (val, decimals = 3) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    if (num === NS_VALUE) return 'НС';
    // ✅ Используем toLocaleString для запятой
    return num.toLocaleString('ru-RU', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

// ✅ Форматирование с точкой → запятой (для toFixed)
const formatWithComma = (val, decimals = 3) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return '—';
    if (num === NS_VALUE) return 'НС';
    return num.toFixed(decimals).replace('.', ',');
};

const formatDateTime = (record) => {
    if (!record) return '—';
    const date = record.date || '';
    const time = record.time || '';
    return `${date} ${time}`.trim();
};

// ============================================
// ✅ Компонент графика с фильтрами
// ============================================
const ArchiveChart = ({ records, columns }) => {
    const [selectedCategory, setSelectedCategory] = useState('temperatura');
    const [selectedChannels, setSelectedChannels] = useState([]);

    const allChannels = useMemo(() => {
        return columns.filter(ch => ch.category);
    }, [columns]);

    const categoryChannels = useMemo(() => {
        return allChannels.filter(ch => ch.category === selectedCategory);
    }, [allChannels, selectedCategory]);

    const availableCategories = useMemo(() => {
        const cats = new Set(allChannels.map(ch => ch.category));
        return Object.keys(PARAM_CATEGORIES).filter(cat => cats.has(cat));
    }, [allChannels]);

    useEffect(() => {
        if (categoryChannels.length > 0) {
            setSelectedChannels([categoryChannels[0].key]);
        } else {
            setSelectedChannels([]);
        }
    }, [selectedCategory, categoryChannels]);

    useEffect(() => {
        if (availableCategories.length > 0 && !availableCategories.includes(selectedCategory)) {
            setSelectedCategory(availableCategories[0]);
        }
    }, [availableCategories]);

    const chartData = useMemo(() => {
        if (!records || records.length === 0) return [];

        return records.map(record => {
            const datetime = formatDateTime(record);
            const point = { datetime, datetimeFull: datetime };

            selectedChannels.forEach(chKey => {
                const val = parseFloat(record[chKey]);
                point[chKey] = (isNaN(val) || val === NS_VALUE) ? null : val;
            });

            return point;
        });
    }, [records, selectedChannels]);

    const toggleChannel = (chKey) => {
        setSelectedChannels(prev =>
            prev.includes(chKey) ? prev.filter(k => k !== chKey) : [...prev, chKey]
        );
    };

    const selectAllChannels = () => setSelectedChannels(categoryChannels.map(ch => ch.key));
    const clearChannels = () => setSelectedChannels([]);

    if (allChannels.length === 0) {
        return (
            <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 text-center">
                <LineChartIcon className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                <p className="text-slate-400">Нет параметров для отображения на графике</p>
            </div>
        );
    }

    const categoryInfo = PARAM_CATEGORIES[selectedCategory];

    return (
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="px-4 py-3 bg-slate-700/50 border-b border-slate-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h3 className="text-white font-semibold flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-400" />
                    График: {categoryInfo?.name || selectedCategory}
                    {categoryInfo?.unit && (
                        <span className="text-xs text-slate-400 font-normal">({categoryInfo.unit})</span>
                    )}
                </h3>
            </div>

            <div className="p-4 border-b border-slate-700 space-y-3">
                <div>
                    <label className="block text-slate-400 text-xs mb-2 uppercase tracking-wide flex items-center gap-1">
                        <Filter className="w-3 h-3" />
                        Категория параметра
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {availableCategories.map(catKey => {
                            const cat = PARAM_CATEGORIES[catKey];
                            const CatIcon = cat.icon;
                            const count = allChannels.filter(ch => ch.category === catKey).length;
                            return (
                                <button
                                    key={catKey}
                                    onClick={() => setSelectedCategory(catKey)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-2 ${selectedCategory === catKey
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                        }`}
                                >
                                    <CatIcon className="w-4 h-4" />
                                    <span>{cat.name}</span>
                                    <span className="text-xs opacity-70">({count})</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-slate-400 text-xs uppercase tracking-wide flex items-center gap-1">
                            <Layers className="w-3 h-3" />
                            Каналы ({selectedChannels.length} из {categoryChannels.length})
                        </label>
                        <div className="flex gap-2">
                            <button onClick={selectAllChannels} className="text-xs text-blue-400 hover:text-blue-300 transition">Выбрать все</button>
                            <button onClick={clearChannels} className="text-xs text-slate-400 hover:text-slate-300 transition">Сбросить</button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {categoryChannels.map((ch, idx) => {
                            const isSelected = selectedChannels.includes(ch.key);
                            const color = CHART_COLORS[idx % CHART_COLORS.length];
                            return (
                                <button
                                    key={ch.key}
                                    onClick={() => toggleChannel(ch.key)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-2 border ${isSelected
                                            ? 'border-transparent text-white'
                                            : 'border-slate-600 bg-slate-900 text-slate-400 hover:border-slate-500'
                                        }`}
                                    style={isSelected ? { backgroundColor: color + '33', borderColor: color } : {}}
                                >
                                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: isSelected ? color : '#475569' }}></span>
                                    <span>{ch.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="p-4">
                {selectedChannels.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                        <TrendingUp className="w-12 h-12 mb-3" />
                        <p className="text-sm">Выберите хотя бы один канал для отображения графика</p>
                    </div>
                ) : chartData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                        <Inbox className="w-12 h-12 mb-3" />
                        <p className="text-sm">Нет данных для отображения</p>
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis dataKey="datetime" stroke="#94a3b8" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                            <YAxis
                                stroke="#94a3b8"
                                tick={{ fontSize: 11 }}
                                tickFormatter={(val) => typeof val === 'number' ? formatWithComma(val, 2) : val}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#1e293b',
                                    border: '1px solid #475569',
                                    borderRadius: '8px',
                                    color: '#f1f5f9',
                                    fontSize: '12px'
                                }}
                                labelStyle={{ color: '#94a3b8', fontWeight: 'bold' }}
                                formatter={(value, name) => {
                                    const ch = allChannels.find(c => c.key === name);
                                    const displayName = ch ? ch.name : name;
                                    const unit = ch ? ch.unit : '';
                                    return [value !== null ? formatWithComma(value, 3) : 'НС', `${displayName}, ${unit}`];
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                            <Brush dataKey="datetime" height={25} stroke="#3b82f6" fill="#1e293b" travellerWidth={8} />
                            {selectedChannels.map((chKey, idx) => {
                                const colorIdx = categoryChannels.findIndex(c => c.key === chKey);
                                const color = CHART_COLORS[(colorIdx >= 0 ? colorIdx : idx) % CHART_COLORS.length];
                                return (
                                    <Line
                                        key={chKey}
                                        type="monotone"
                                        dataKey={chKey}
                                        name={chKey}
                                        stroke={color}
                                        strokeWidth={2}
                                        dot={chartData.length <= 50}
                                        activeDot={{ r: 5, strokeWidth: 2 }}
                                        connectNulls={false}
                                        isAnimationActive={false}
                                    />
                                );
                            })}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

// ============================================
// ✅ Компонент раскрытого списка НС
// ============================================
const NSListExpanded = ({ messages, onClose }) => {
    if (!messages || messages.length === 0) return null;
    const errors = messages.filter(m => m.type === 'error');
    const warnings = messages.filter(m => m.type === 'warning');

    return (
        <tr>
            <td colSpan={100} className="p-0">
                <div className="bg-slate-950 border-y border-yellow-500/30">
                    <div className="flex items-center justify-between px-4 py-2 bg-yellow-500/10 border-b border-yellow-500/20">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-yellow-400" />
                            <span className="text-yellow-200 font-semibold text-sm">
                                Нештатные ситуации ({messages.length})
                            </span>
                            {errors.length > 0 && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-300 rounded text-xs flex items-center gap-1">
                                    <XCircle className="w-3 h-3" />
                                    Аварий: {errors.length}
                                </span>
                            )}
                            {warnings.length > 0 && (
                                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-300 rounded text-xs flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    Предупреждений: {warnings.length}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-white transition p-1 rounded hover:bg-slate-700"
                            title="Закрыть"
                        >
                            <XCircle className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto custom-scrollbar">
                        {errors.length > 0 && (
                            <div className="px-4 py-2">
                                <div className="text-red-400 text-xs font-semibold mb-2 uppercase tracking-wide flex items-center gap-1">
                                    <XCircle className="w-3 h-3" />
                                    Аварии ({errors.length})
                                </div>
                                <div className="space-y-1">
                                    {errors.map((msg, idx) => (
                                        <div key={`err-${idx}`} className="flex items-start gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded text-sm">
                                            <span className="text-red-400 flex-shrink-0">🔴</span>
                                            <span className="text-red-200">{msg.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {warnings.length > 0 && (
                            <div className="px-4 py-2">
                                <div className="text-yellow-400 text-xs font-semibold mb-2 uppercase tracking-wide flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    Предупреждения ({warnings.length})
                                </div>
                                <div className="space-y-1">
                                    {warnings.map((msg, idx) => (
                                        <div key={`warn-${idx}`} className="flex items-start gap-2 px-3 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded text-sm">
                                            <span className="text-yellow-400 flex-shrink-0">🟡</span>
                                            <span className="text-yellow-200">{msg.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </td>
        </tr>
    );
};

// ============================================
// ✅ Генерация списка НС
// ============================================
const buildNSMessagesForRecord = (record) => {
    const messages = [];
    if (!record) return messages;

    const nsTemperatura = parseInt(record.NS_temperatura) || 0;
    const nsDavlenie = parseInt(record.NS_davlenie) || 0;
    const nsRasxod = parseInt(record.NS_rasxod) || 0;
    const nsEnergi = parseInt(record.NS_energi_pitanie) || 0;

    for (let i = 0; i < 4; i++) {
        if ((nsTemperatura & (1 << i)) !== 0) {
            messages.push({ type: 'error', text: `АВАРИЯ: Температура датчика t${i + 1} вне диапазона` });
        }
    }
    if ((nsTemperatura & 16) !== 0) messages.push({ type: 'error', text: 'АВАРИЯ: Температура холодной воды (tx) вне диапазона' });
    if ((nsTemperatura & 32) !== 0) messages.push({ type: 'error', text: 'АВАРИЯ: Разность температур (dto) меньше 1 град.' });
    if ((nsTemperatura & 64) !== 0) messages.push({ type: 'error', text: 'АВАРИЯ: Разность температур (dtg) меньше 1 град.' });
    if ((nsTemperatura & 128) !== 0) messages.push({ type: 'warning', text: 'ПРЕДУПРЕЖДЕНИЕ: Температура окружающего воздуха (tok) вне диапазона' });

    for (let i = 0; i < 4; i++) {
        if ((nsDavlenie & (1 << i)) !== 0) {
            messages.push({ type: 'warning', text: `ПРЕДУПРЕЖДЕНИЕ: Давление датчика P${i + 1} вне диапазона` });
        }
    }
    if ((nsDavlenie & 16) !== 0) messages.push({ type: 'warning', text: 'ПРЕДУПРЕЖДЕНИЕ: Давление холодной воды (Px) вне диапазона' });
    if ((nsDavlenie & 32) !== 0) messages.push({ type: 'warning', text: 'ПРЕДУПРЕЖДЕНИЕ: Отрицательная разность давлений (dPo)' });
    if ((nsDavlenie & 64) !== 0) messages.push({ type: 'warning', text: 'ПРЕДУПРЕЖДЕНИЕ: Отрицательная разность давлений (dPg)' });

    for (let i = 0; i < 4; i++) {
        if ((nsRasxod & (1 << i)) !== 0) {
            messages.push({ type: 'warning', text: `ПРЕДУПРЕЖДЕНИЕ: Расход датчика Go${i + 1} вне диапазона` });
        }
    }
    if ((nsRasxod & 16) !== 0) messages.push({ type: 'warning', text: 'ПРЕДУПРЕЖДЕНИЕ: Расход холодной воды (Gox) вне диапазона' });
    if ((nsRasxod & 32) !== 0) messages.push({ type: 'error', text: 'АВАРИЯ: Расход (Goo) вне диапазона' });
    if ((nsRasxod & 64) !== 0) messages.push({ type: 'error', text: 'АВАРИЯ: Расход (Gog) вне диапазона' });
    if ((nsRasxod & 128) !== 0) messages.push({ type: 'warning', text: 'ПРЕДУПРЕЖДЕНИЕ: Расход (Go) меньше переходного (Gper)' });

    if ((nsEnergi & 1) !== 0) messages.push({ type: 'error', text: 'АВАРИЯ: Тепловая энергия Qo вне диапазона' });
    if ((nsEnergi & 2) !== 0) messages.push({ type: 'error', text: 'АВАРИЯ: Тепловая энергия Qg вне диапазона' });
    if ((nsEnergi & 16) !== 0) messages.push({ type: 'warning', text: 'ВНИМАНИЕ: Внешнее питание отключено' });
    if ((nsEnergi & 32) !== 0) messages.push({ type: 'warning', text: 'ВНИМАНИЕ: Перезапуск ПО' });

    return messages;
};

// ============================================
// ✅ Экспорт в Excel (с адресом и серийным номером, запятая как разделитель)
// ============================================
const exportToExcel = (records, columns, archiveType, dateFrom, dateTo, deviceName, serialNumber, address) => {
    if (!records || records.length === 0) {
        alert('Нет данных для экспорта');
        return;
    }

    const headers = columns.map(col => {
        if (col.type === 'datetime') return 'Дата/Время';
        if (col.type === 'ns') return 'НС';
        if (col.type === 'time') return `${col.name} (${col.unit})`;
        return `${col.name}, ${col.unit}`;
    });

    // Строки данных (с запятой как разделителем)
    const rows = records.map(record => {
        const values = columns.map(col => {
            if (col.type === 'datetime') {
                return formatDateTime(record);
            }
            if (col.type === 'ns') {
                const nsMessages = buildNSMessagesForRecord(record);
                return nsMessages.length;
            }
            if (col.type === 'time') {
                const val = parseFloat(record[col.source]) / 60;
                return isNaN(val) ? '' : formatWithComma(val, 3);
            }
            const val = parseFloat(record[col.key]);
            if (isNaN(val)) return '';
            if (val === NS_VALUE) return 'НС';
            return formatWithComma(val, 3);
        });

        return values.map(v => `"${v}"`).join(';');
    });

    const archiveTypeNames = {
        'hour': 'Часовой',
        'day': 'Суточный',
        'month': 'Месячный',
        'integral': 'Интегральный'
    };

    // ✅ Мета-информация с адресом и серийным номером
    const metaInfo = [
        `Аксиома-01: ${deviceName}`,
        `Серийный номер: ${serialNumber || '—'}`,
        `Адрес: ${address || '—'}`,
        `Тип архива: ${archiveTypeNames[archiveType] || archiveType}`,
        `Период: ${new Date(dateFrom).toLocaleDateString('ru-RU')} - ${new Date(dateTo).toLocaleDateString('ru-RU')}`,
        `Количество записей: ${records.length}`,
        '',
        headers.map(h => `"${h}"`).join(';')
    ];

    const csvContent = [...metaInfo, ...rows].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    link.setAttribute('href', url);
    link.setAttribute('download', `aksioma01_${deviceName}_${archiveType}_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// ============================================
// ✅ Главный компонент вкладки "Архив"
// ============================================
const ArchiveTab = ({ device }) => {
    const [archiveType, setArchiveType] = useState('day');
    const [loadedArchiveType, setLoadedArchiveType] = useState('day');
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
    const [archiveData, setArchiveData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedNSRecord, setExpandedNSRecord] = useState(null);
    const [showChart, setShowChart] = useState(true);

    // ✅ Новое состояние для информации об устройстве (серийный номер)
    const [deviceInfo, setDeviceInfo] = useState(null);

    const PAGE_SIZE = 50;
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const sentinelRef = useRef(null);
    const tableContainerRef = useRef(null);

    const archiveTypes = [
        { id: 'hour', name: 'Часовой', icon: Clock },
        { id: 'day', name: 'Суточный', icon: Calendar },
        { id: 'month', name: 'Месячный', icon: Calendar },
        { id: 'integral', name: 'Интегральный', icon: LineChartIcon },
    ];

    const archiveTypeNames = {
        'hour': 'Часовой',
        'day': 'Суточный',
        'month': 'Месячный',
        'integral': 'Интегральный'
    };

    // ✅ Загрузка информации об устройстве (серийный номер)
    useEffect(() => {
        loadDeviceInfo();
    }, [device.name]);

    const loadDeviceInfo = async () => {
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.REACT_APP_API_URL;
            const response = await axios.get(
                `${API_URL}/api/devices/aksioma01/${device.name}/info`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setDeviceInfo(response.data);
        } catch (err) {
            console.error('Ошибка загрузки информации об устройстве:', err);
        }
    };

    const loadArchive = async () => {
        setIsLoading(true);
        setError(null);
        setExpandedNSRecord(null);
        setVisibleCount(PAGE_SIZE);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.REACT_APP_API_URL;

            const response = await axios.get(
                `${API_URL}/api/devices/aksioma01/${device.name}/archive`,
                {
                    params: { type: archiveType, from: dateFrom, to: dateTo },
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            console.log('📊 Archive:', response.data);
            setArchiveData(response.data);
            setLoadedArchiveType(archiveType);
        } catch (err) {
            console.error('Ошибка загрузки архива:', err);
            setError(err.response?.data?.message || 'Не удалось загрузить архивные данные');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadArchive();
    }, [device.name]);

    useEffect(() => {
        if (!sentinelRef.current || !archiveData) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && archiveData) {
                    const totalRecs = archiveData.records?.length || 0;
                    if (visibleCount < totalRecs && !isLoadingMore) {
                        setIsLoadingMore(true);
                        setTimeout(() => {
                            setVisibleCount(prev => Math.min(prev + PAGE_SIZE, totalRecs));
                            setIsLoadingMore(false);
                        }, 150);
                    }
                }
            },
            {
                root: tableContainerRef.current,
                rootMargin: '200px',
                threshold: 0.1
            }
        );

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [archiveData, visibleCount, isLoadingMore]);

    const records = archiveData?.records || [];
    const totalRecords = records.length;

    const columns = useMemo(() => {
        if (!records || records.length === 0) return [];
        const sampleRecord = records[0];
        if (loadedArchiveType === 'integral') {
            return buildIntegralColumns(sampleRecord);
        }
        return buildArchiveColumns(sampleRecord, loadedArchiveType);
    }, [records, loadedArchiveType]);

    const visibleRecords = useMemo(() => {
        return [...records].reverse().slice(0, visibleCount);
    }, [records, visibleCount]);

    const hasMore = visibleCount < totalRecords;
    const isChartAvailable = loadedArchiveType !== 'integral';
    const isArchiveTypeChanged = archiveType !== loadedArchiveType;

    const handleNSCellClick = (recordIdx) => {
        if (expandedNSRecord === recordIdx) {
            setExpandedNSRecord(null);
        } else {
            setExpandedNSRecord(recordIdx);
        }
    };

    const handleShowAll = () => setVisibleCount(totalRecords);

    const handleShowLess = () => {
        setVisibleCount(PAGE_SIZE);
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
        }
    };

    // ✅ Форматирование адреса
    const getAddress = () => {
        if (!device) return '—';
        const parts = [];
        if (device.city) parts.push(device.city);
        if (device.street) parts.push(device.street);
        if (device.houseNumber) parts.push(`дом ${device.houseNumber}`);
        return parts.length > 0 ? parts.join(', ') : '—';
    };

    // ✅ Получение серийного номера
    const getSerialNumber = () => {
        return deviceInfo?.serial_number || '—';
    };

    // ✅ Получение значения ячейки (с запятой как разделителем)
    const getCellValue = (record, col) => {
        if (col.type === 'datetime') {
            return formatDateTime(record);
        }
        if (col.type === 'ns') {
            const nsMessages = buildNSMessagesForRecord(record);
            return nsMessages.length;
        }
        if (col.type === 'time') {
            const val = parseFloat(record[col.source]) / 60;
            return isNaN(val) ? '—' : formatWithComma(val, 3);
        }
        const val = parseFloat(record[col.key]);
        if (isNaN(val)) return '—';
        if (val === NS_VALUE) return 'НС';
        return formatWithComma(val, 3);
    };

    return (
        <div className="space-y-4">
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Archive className="w-5 h-5 text-blue-400" />
                    Архивные данные
                </h3>

                <div className="mb-4">
                    <label className="block text-slate-400 text-xs mb-2">Тип архива</label>
                    <div className="flex flex-wrap gap-2">
                        {archiveTypes.map(p => {
                            const Icon = p.icon;
                            return (
                                <button
                                    key={p.id}
                                    onClick={() => setArchiveType(p.id)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${archiveType === p.id
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                        }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    <span>{p.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
                    <div>
                        <label className="block text-slate-400 text-xs mb-1">С даты</label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-slate-400 text-xs mb-1">По дату</label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={loadArchive}
                            disabled={isLoading}
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Загрузка...
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4" />
                                    Загрузить
                                </>
                            )}
                        </button>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => exportToExcel(
                                records,
                                columns,
                                loadedArchiveType,
                                dateFrom,
                                dateTo,
                                device.name,
                                getSerialNumber(),
                                getAddress()
                            )}
                            disabled={!archiveData || records.length === 0}
                            className="w-full px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Выгрузить архив в Excel (CSV)"
                        >
                            <FileSpreadsheet className="w-4 h-4" />
                            Экспорт в Excel
                        </button>
                    </div>
                </div>

                {isArchiveTypeChanged && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                        <div className="flex-1">
                            <p className="text-yellow-200 text-sm font-medium">
                                Выбран тип архива: <strong>{archiveTypeNames[archiveType]}</strong>
                            </p>
                            <p className="text-yellow-300/80 text-xs mt-1">
                                Отображаются данные за загруженный тип: <strong>{archiveTypeNames[loadedArchiveType]}</strong>.
                                Нажмите "Загрузить" для обновления.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {!archiveData || totalRecords === 0 ? (
                <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                    <Inbox className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                    <p className="text-slate-400">
                        {isLoading ? 'Загрузка данных...' : 'Нет данных за выбранный период'}
                    </p>
                </div>
            ) : (
                <>
                    {isChartAvailable && showChart && (
                        <div className="relative">
                            <button
                                onClick={() => setShowChart(false)}
                                className="absolute top-2 right-2 z-10 p-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition flex items-center gap-1"
                                title="Скрыть график"
                            >
                                <EyeOff className="w-4 h-4" />
                                <span className="text-xs">Скрыть</span>
                            </button>
                            <ArchiveChart records={records} columns={columns} />
                        </div>
                    )}

                    {isChartAvailable && !showChart && (
                        <button
                            onClick={() => setShowChart(true)}
                            className="w-full px-4 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 rounded-xl text-sm font-medium transition flex items-center justify-center gap-2"
                        >
                            <Eye className="w-4 h-4" />
                            Показать график
                        </button>
                    )}

                    {!isChartAvailable && (
                        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                            <Info className="w-4 h-4" />
                            График недоступен для интегрального архива. Используйте таблицу ниже.
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-2">
                        <div className="text-slate-400 text-sm flex items-center gap-1">
                            <Layers className="w-3 h-3" />
                            Показано <span className="text-white font-semibold">{Math.min(visibleCount, totalRecords)}</span> из{' '}
                            <span className="text-white font-semibold">{totalRecords}</span> записей
                        </div>
                        <div className="flex gap-2">
                            {visibleCount > PAGE_SIZE && (
                                <button
                                    onClick={handleShowLess}
                                    className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-xs font-medium transition flex items-center gap-1"
                                >
                                    <ChevronRight className="w-3 h-3 rotate-[-90deg]" />
                                    Свернуть
                                </button>
                            )}
                            {hasMore && (
                                <button
                                    onClick={handleShowAll}
                                    className="px-3 py-1 bg-blue-600/30 hover:bg-blue-600/50 text-blue-300 rounded text-xs font-medium transition"
                                >
                                    Показать все ({totalRecords})
                                </button>
                            )}
                        </div>
                    </div>

                    <div
                        ref={tableContainerRef}
                        className="bg-slate-800 rounded-xl border border-slate-700 overflow-auto custom-scrollbar"
                        style={{ maxHeight: 'calc(100vh - 380px)', minHeight: '400px' }}
                    >
                        <table className="w-full text-sm">
                            <thead className="bg-slate-700/80 backdrop-blur-sm sticky top-0 z-20 shadow-md">
                                <tr>
                                    {columns.map((col, idx) => (
                                        <th
                                            key={idx}
                                            className={`px-3 py-3 font-semibold whitespace-nowrap border-r border-slate-600 last:border-r-0 bg-slate-800 text-slate-200 ${col.type === 'datetime' || col.type === 'ns' ? 'text-left' : 'text-right'
                                                }`}
                                        >
                                            <div className="flex flex-col">
                                                <span>{col.type === 'datetime' ? 'Дата/Время' : col.type === 'ns' ? 'НС' : col.name}</span>
                                                {col.unit && col.type !== 'datetime' && col.type !== 'ns' && (
                                                    <span className="text-xs text-slate-400 font-normal">{col.unit}</span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRecords.map((record, recordIdx) => {
                                    const isExpanded = expandedNSRecord === recordIdx;
                                    const nsMessages = buildNSMessagesForRecord(record);
                                    const nsCount = nsMessages.length;

                                    return (
                                        <React.Fragment key={recordIdx}>
                                            <tr className={`border-t border-slate-700 hover:bg-slate-700/30 transition ${isExpanded ? 'bg-yellow-500/5' : ''}`}>
                                                {columns.map((col, colIdx) => {
                                                    const value = getCellValue(record, col);
                                                    const isNS = value === 'НС';
                                                    const isNSCount = col.type === 'ns';

                                                    if (isNSCount) {
                                                        return (
                                                            <td
                                                                key={colIdx}
                                                                className={`px-3 py-2 whitespace-nowrap border-r border-slate-700 text-center ${nsCount > 0 ? 'cursor-pointer hover:bg-yellow-500/20 transition' : ''}`}
                                                                onClick={() => nsCount > 0 && handleNSCellClick(recordIdx)}
                                                                title={nsCount > 0 ? 'Нажмите для просмотра списка НС' : ''}
                                                            >
                                                                {nsCount > 0 ? (
                                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded font-semibold transition ${isExpanded
                                                                            ? 'bg-yellow-500/30 text-yellow-200 ring-2 ring-yellow-500/50'
                                                                            : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                                                                        }`}>
                                                                        <AlertTriangle className="w-3 h-3" />
                                                                        <span>({nsCount})</span>
                                                                        <ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                                                                    </span>
                                                                ) : (
                                                                    <CheckCircle2 className="w-4 h-4 text-green-400 inline-block" />
                                                                )}
                                                            </td>
                                                        );
                                                    }

                                                    return (
                                                        <td
                                                            key={colIdx}
                                                            className={`px-3 py-2 whitespace-nowrap border-r border-slate-700 last:border-r-0 ${col.type === 'datetime' || col.type === 'ns' ? 'text-left text-white' : 'text-right font-mono'
                                                                } ${isNS ? 'text-red-400 font-bold bg-red-500/10' : 'text-white'}`}
                                                        >
                                                            {value}
                                                        </td>
                                                    );
                                                })}
                                            </tr>

                                            {isExpanded && (
                                                <NSListExpanded
                                                    messages={nsMessages}
                                                    onClose={() => setExpandedNSRecord(null)}
                                                />
                                            )}
                                        </React.Fragment>
                                    );
                                })}

                                {hasMore && (
                                    <tr ref={sentinelRef}>
                                        <td colSpan={columns.length} className="p-4 text-center">
                                            {isLoadingMore ? (
                                                <div className="flex items-center justify-center gap-2 text-slate-400">
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    <span className="text-sm">Загрузка ещё {Math.min(PAGE_SIZE, totalRecords - visibleCount)} записей...</span>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setVisibleCount(prev => Math.min(prev + PAGE_SIZE, totalRecords))}
                                                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-medium transition"
                                                >
                                                    Показать ещё ({Math.min(PAGE_SIZE, totalRecords - visibleCount)} из {totalRecords - visibleCount})
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )}

                                {!hasMore && totalRecords > PAGE_SIZE && (
                                    <tr>
                                        <td colSpan={columns.length} className="p-3 text-center text-slate-500 text-xs">
                                            — Конец списка • Показано все {totalRecords} записей —
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default ArchiveTab;